import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { TestSetsSection as TestSetsSectionType } from '../../../types';

interface TestSetsSectionProps {
  data?: TestSetsSectionType;
  fallbackPosts?: any[];
}

// Fallback data when API response is null or invalid
const DEFAULT_TEST_SETS_DATA: TestSetsSectionType = {
  title: 'Bài Viết IELTS Mới Nhất',
  description: 'Cập nhật các bài viết và tài liệu IELTS hữu ích từ các chuyên gia IELTS hàng đầu',
};

const DEFAULT_TEST_SETS_POSTS = [
  {
    id: '1',
    title: 'IELTS Listening Tips & Tricks',
    slug: 'ielts-listening-tips',
    excerpt: 'Learn advanced techniques to improve your IELTS listening score with confidence.',
    blogType: 'ielts',
    difficulty: 'intermediate',
    ieltsFields: {
      start: 4.5,
      practiceTime: 45,
      badge: 'hot',
      skills: ['listening'],
      difficulty: 'intermediate',
    },
  },
  {
    id: '2',
    title: 'Reading Comprehension Strategies',
    slug: 'reading-comprehension',
    excerpt: 'Master the essential reading skills required for high IELTS scores.',
    blogType: 'ielts',
    difficulty: 'advanced',
    ieltsFields: {
      start: 4.8,
      practiceTime: 60,
      badge: 'premium',
      skills: ['reading', 'writing'],
      difficulty: 'advanced',
    },
  },
  {
    id: '3',
    title: 'Writing Task 1: Direct Method',
    slug: 'writing-task-1',
    excerpt: 'Complete guide to writing task 1 with examples and practice exercises.',
    blogType: 'ielts',
    difficulty: 'beginner',
    ieltsFields: {
      start: 4,
      practiceTime: 50,
      badge: null,
      skills: ['writing'],
      difficulty: 'beginner',
    },
  },
  {
    id: '4',
    title: 'Speaking Part 2: Success Formula',
    slug: 'speaking-part-2',
    excerpt: 'Proven strategies for excelling in IELTS speaking part 2 with clear examples.',
    blogType: 'ielts',
    difficulty: 'intermediate',
    ieltsFields: {
      start: 4.5,
      practiceTime: 40,
      badge: 'popular',
      skills: ['speaking'],
      difficulty: 'intermediate',
    },
  },
];

const IELTSMaterials: React.FC<TestSetsSectionProps> = ({
  data = DEFAULT_TEST_SETS_DATA,
  fallbackPosts = DEFAULT_TEST_SETS_POSTS,
}) => {
  const [posts] = useState(fallbackPosts);

  // Refs and animation setup
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const sectionData = data || DEFAULT_TEST_SETS_DATA;

  // Animation variants
  const containerVariants = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 80,
        damping: 15,
      },
    },
  };

  // Get difficulty icon
  const getDifficultyIcon = (difficulty?: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return '⭐';
      case 'intermediate':
        return '📚';
      case 'advanced':
        return '🎓';
      default:
        return '✓';
    }
  };

  // Render stars based on rating
  const renderStars = (rating = 4) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="text-yellow-400">
            ★
          </span>
        ))}
        {hasHalfStar && <span className="text-yellow-400">★</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="text-gray-400">
            ☆
          </span>
        ))}
      </div>
    );
  };

  // Blog card component
  const BlogCard = ({ post, index }: { post: any; index: number }) => {
    const badge = post.ieltsFields?.badge;
    const badgeColor =
      badge?.toLowerCase() === 'hot'
        ? 'bg-red-600'
        : badge?.toLowerCase() === 'popular'
          ? 'bg-orange-600'
          : 'bg-[#017e6e]';

    return (
      <motion.div
        variants={itemVariants}
        className="test-card bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 h-full flex flex-col relative transition-all duration-300 hover:bg-white/15 hover:-translate-y-2 group"
      >
        {/* Badge */}
        {badge && (
          <motion.div
            className="absolute top-3 right-4 z-10"
            initial={{ scale: 0, rotate: -45 }}
            animate={isInView ? { scale: 1, rotate: 0 } : {}}
            transition={{ delay: 0.2 + index * 0.1 }}
          >
            <div className={`${badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
              {badge.toUpperCase()}
            </div>
          </motion.div>
        )}

        {/* Content section */}
        <div className="p-6 flex flex-col flex-grow">
          <div className="flex items-start mb-4">
            <motion.div
              className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4 flex-shrink-0 backdrop-blur-sm border border-white/30 transition-transform duration-300 group-hover:scale-110"
              whileHover={{ scale: 1.1 }}
            >
              <span className="text-white text-lg">{getDifficultyIcon(post.ieltsFields?.difficulty)}</span>
            </motion.div>

            <h3 className="text-lg font-bold text-white mt-1 line-clamp-2">{post.title}</h3>
          </div>

          <p className="text-white/80 text-sm mb-5 flex-grow line-clamp-3">{post.excerpt}</p>

          <div className="flex justify-between items-center mt-auto pt-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              {renderStars(post.ieltsFields?.start || 4)}
              <span className="text-white/70 text-xs">({post.ieltsFields?.start || 4})</span>
            </div>
            <div className="bg-white/20 text-white rounded-full px-3 py-1 text-xs font-medium backdrop-blur-sm">
              {post.ieltsFields?.practiceTime || 45} phút
            </div>
          </div>
        </div>

        {/* Action button */}
        <div className="px-6 pb-6">
          <motion.a
            href={`#`}
            className="w-full bg-white text-[#005e5d] hover:bg-white/90 py-3 rounded-full font-medium transition-all duration-300 flex items-center justify-center group-hover:scale-[1.02] active:scale-[0.98]"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Xem Chi Tiết
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.a>
        </div>
      </motion.div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-r from-[#005e5d] to-[#017e6e] text-white blog-posts-section"
      id="test-sets"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center lg:mb-6 sm:mb-8 md:mb-12"
        >
          <div className="mb-4">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center justify-center mb-3"
            >
              <span className="h-0.5 w-6 sm:w-8 bg-white/60 mr-2 sm:mr-3"></span>
              <h3 className="text-white/90 text-xs sm:text-sm font-medium uppercase tracking-wider">IELTS Materials</h3>
              <span className="h-0.5 w-6 sm:w-8 bg-white/60 ml-2 sm:ml-3"></span>
            </motion.div>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4"
          >
            {sectionData.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="max-w-2xl mx-auto text-sm sm:text-base text-white/80"
          >
            {sectionData.description}
          </motion.p>
        </motion.div>

        {/* Blog Cards Grid */}
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {posts.map((post, index) => (
            <BlogCard key={post.id} post={post} index={index} />
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <motion.a
            href="#"
            className="inline-block bg-white text-[#005e5d] hover:bg-white/90 px-8 py-3 rounded-full font-semibold transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Xem Thêm Bài Viết
          </motion.a>
        </motion.div>
      </div>

      <style>{`
        .blog-posts-section {
          position: relative;
        }

        @media (max-width: 1279px) {
          .blog-posts-section {
            overflow-x: hidden;
          }
        }
      `}</style>
    </section>
  );
};

export default IELTSMaterials;
